import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, RotateCcw, Save } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  SITE_SETTINGS_SCHEMA,
  validateSetting,
  type SettingFieldDef,
} from "@/lib/siteSettingsSchema";

interface SettingRow {
  id: string;
  setting_key: string;
  setting_type: string;
  setting_value: string | null;
  is_active: boolean;
}

const AdminSettings = () => {
  const { user } = useAdminAuth();
  const [groupKey, setGroupKey] = useState<string>(SITE_SETTINGS_SCHEMA[0].group_key);
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const group = useMemo(
    () => SITE_SETTINGS_SCHEMA.find((g) => g.group_key === groupKey)!,
    [groupKey],
  );

  useEffect(() => {
    document.title = "Settings | Hadassah Homes Admin";
  }, []);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("site_settings")
      .select("id, setting_key, setting_type, setting_value, is_active");
    if (err) {
      setError(err.message);
      setRows([]);
    } else {
      setRows((data ?? []) as unknown as SettingRow[]);
    }
    setDrafts({});
    setLoading(false);
  };

  const rowFor = (key: string) => rows.find((r) => r.setting_key === key);

  const currentValue = (key: string): string => {
    if (key in drafts) return drafts[key];
    return rowFor(key)?.setting_value ?? "";
  };

  const setDraft = (key: string, value: string) =>
    setDrafts((p) => ({ ...p, [key]: value }));

  const handleSave = async (field: SettingFieldDef) => {
    const value = currentValue(field.setting_key).trim();
    const validationError = validateSetting(field.type, value);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSavingKey(field.setting_key);
    const existing = rowFor(field.setting_key);
    if (existing) {
      const { data, error: err } = await supabase
        .from("site_settings")
        .update({
          setting_value: value || null,
          setting_type: field.type,
          is_active: true,
          updated_by: user?.id ?? null,
        } as never)
        .eq("id", existing.id)
        .select()
        .single();
      setSavingKey(null);
      if (err) return toast.error(err.message);
      setRows((p) => p.map((r) => (r.id === existing.id ? (data as unknown as SettingRow) : r)));
      setDrafts((p) => {
        const { [field.setting_key]: _o, ...rest } = p;
        return rest;
      });
      toast.success("Saved");
    } else {
      if (!value) {
        setSavingKey(null);
        toast.error("Enter a value before saving the first override");
        return;
      }
      const { data, error: err } = await supabase
        .from("site_settings")
        .insert({
          setting_key: field.setting_key,
          setting_type: field.type,
          setting_value: value,
          group_key: group.group_key,
          label: field.label,
          is_active: true,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        } as never)
        .select()
        .single();
      setSavingKey(null);
      if (err) return toast.error(err.message);
      setRows((p) => [...p, data as unknown as SettingRow]);
      setDrafts((p) => {
        const { [field.setting_key]: _o, ...rest } = p;
        return rest;
      });
      toast.success("Override created");
    }
  };

  const handleReset = async (field: SettingFieldDef) => {
    const existing = rowFor(field.setting_key);
    if (!existing) {
      setDrafts((p) => {
        const { [field.setting_key]: _o, ...rest } = p;
        return rest;
      });
      return;
    }
    setSavingKey(field.setting_key);
    const { data, error: err } = await supabase
      .from("site_settings")
      .update({ is_active: false, updated_by: user?.id ?? null } as never)
      .eq("id", existing.id)
      .select()
      .single();
    setSavingKey(null);
    if (err) return toast.error(err.message);
    setRows((p) => p.map((r) => (r.id === existing.id ? (data as unknown as SettingRow) : r)));
    setDrafts((p) => {
      const { [field.setting_key]: _o, ...rest } = p;
      return rest;
    });
    toast.success("Reverted to fallback (override deactivated)");
  };

  return (
    <AdminLayout
      title="Site Settings"
      description="Manage global business info such as phone, email, social links, and footer content. Empty values fall back to the built-in website value."
      publicHref="/"
      publicLabel="View website"
    >
      <div className="mx-auto max-w-4xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Edit global business info used across the site. Each field has two states:{" "}
              <span className="font-semibold text-foreground">Override active</span> (your value is shown publicly) or{" "}
              <span className="font-semibold text-foreground">Using fallback</span> (the built-in value is shown).
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Use <span className="font-semibold">Reset to fallback</span> to deactivate an override without deleting it.
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={groupKey}
              onChange={(e) => setGroupKey(e.target.value)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              {SITE_SETTINGS_SCHEMA.map((g) => (
                <option key={g.group_key} value={g.group_key}>
                  {g.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              onClick={() => void load()}
              disabled={loading}
              className="rounded-none"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 border border-border bg-card">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="border border-destructive/40 bg-destructive/5 p-6 text-destructive">{error}</div>
        ) : (
          <section className="border border-border bg-card">
            <header className="border-b border-border bg-muted/40 px-5 py-3">
              <h2 className="font-headline text-lg text-foreground">{group.label}</h2>
              {group.description ? (
                <p className="text-[11px] text-muted-foreground">{group.description}</p>
              ) : null}
            </header>
            <div className="divide-y divide-border">
              {group.fields.map((field) => {
                const row = rowFor(field.setting_key);
                const overrideActive = !!row && row.is_active;
                const dirty = field.setting_key in drafts;
                const value = currentValue(field.setting_key);
                const saving = savingKey === field.setting_key;
                return (
                  <div
                    key={field.setting_key}
                    className="grid grid-cols-1 gap-3 p-5 md:grid-cols-[220px_1fr]"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{field.label}</p>
                      <p className="text-[11px] text-muted-foreground">{field.setting_key}</p>
                      {field.helper ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">{field.helper}</p>
                      ) : null}
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            overrideActive
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                              : row
                                ? "border-border bg-muted text-muted-foreground"
                                : "border-border bg-background text-muted-foreground"
                          }`}
                        >
                          {overrideActive
                            ? "Override active"
                            : row
                              ? "Inactive (fallback)"
                              : "Using fallback"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {field.type === "textarea" ? (
                        <Textarea
                          rows={3}
                          value={value}
                          onChange={(e) => setDraft(field.setting_key, e.target.value)}
                          placeholder={field.placeholder ?? "Leave empty to use fallback"}
                          className="rounded-none"
                        />
                      ) : (
                        <Input
                          value={value}
                          onChange={(e) => setDraft(field.setting_key, e.target.value)}
                          placeholder={field.placeholder ?? "Leave empty to use fallback"}
                          className="rounded-none"
                          inputMode={
                            field.type === "phone"
                              ? "tel"
                              : field.type === "email"
                                ? "email"
                                : field.type === "url" || field.type === "image_url"
                                  ? "url"
                                  : "text"
                          }
                        />
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="gold"
                          disabled={saving || (!dirty && overrideActive)}
                          onClick={() => void handleSave(field)}
                          className="rounded-none"
                        >
                          {saving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          {row ? "Save override" : "Create override"}
                        </Button>
                        {row ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={saving}
                            onClick={() => void handleReset(field)}
                            className="rounded-none"
                            title="Deactivates this override; site shows built-in fallback. Not deleted."
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reset to fallback
                          </Button>
                        ) : null}
                        {dirty ? (
                          <span className="text-[11px] text-muted-foreground">Unsaved changes</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
